import { FC, useState } from 'react';
import { ImageWrapper } from '@apps/components/CarCard';
import QuoteDialog from '@apps/components/CarDetails/CarHighlight/QuoteDialog';
import StyledImage from '@apps/components/Image';
import RankTag from '@apps/components/RankTag';
import SoldChip from '@apps/components/SoldChip';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import { priceFormat } from '@apps/utils/format-price';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import createBasePath from '@stack/helpers/lib/createBasePath';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import NextLink from 'next/link';

import { TopModelItemProps } from './props';

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const TopModelItem: FC<TopModelItemProps> = (props) => {
  const {
    new_car_title,
    rank,
    total_sold,
    total_search,
    car_model_image,
    lowest_price,
    highest_price,
    type,
    new_car_id,
    car_brand_image,
    location_state,
    location_city,
    webView = false,
    car_brand_slug,
    new_car_slug,
  } = props;
  // ============ HOOKS
  const { clickAction } = usePostMsgContext();

  const { lgUp, smUp } = useGetResponsive();

  const { basePath } = createBasePath({ webView, type: 'cars' });
  const currentPath = `${basePath}/ranking`;

  const newCarHref = `${basePath}/${car_brand_slug}/${new_car_slug}`;
  const clickProps = {
    component: NextLink,
    href: newCarHref,
    onClick: (event: { preventDefault: () => void }) => {
      if (webView) {
        event.preventDefault();
        clickAction(newCarHref, false, {
          title: new_car_title,
          params: {
            brand: car_brand_slug,
            slug: new_car_slug,
          },
        });
      }
    },
  };

  // ============ EVENTS
  const onOpenGetQuoteDialog = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    setIsOpen(true);
    if (webView) {
      clickAction(currentPath, false, {
        title: 'Get Quote',
        params: {
          headerShown: false,
          action: 'headerShown',
        },
      });
    }
  };

  const onClose = () => {
    setIsOpen(false);
    if (webView) {
      clickAction(currentPath, false, {
        title: 'Get Quote',
        params: {
          headerShown: true,
          action: 'headerShown',
        },
      });
    }
  };

  // ============ STATE
  const [isOpen, setIsOpen] = useState(false);

  // ============ VIEWS
  const renderChip = () => {
    if (type === 'Sold') {
      return <SoldChip label={total_sold} postfix={type} />;
    }
    return <SoldChip label={total_search} postfix={type} />;
  };

  const renderContent = () => {
    if (lgUp) {
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          style={{
            borderBottom: '1px solid #EDECED',
            padding: '16px 0',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <Stack
            direction={'row'}
            {...clickProps}
            sx={{
              gap: 16,
              alignItems: 'center',
            }}
          >
            <Stack
              direction="row"
              gap={4}
              alignItems="center"
              style={{ minWidth: '40%' }}
            >
              <RankTag rank={rank ?? 0} />
              <ImageWrapper height="92px" width="176px">
                <StyledImage
                  src={car_model_image}
                  alt={new_car_title}
                  height={92}
                  width={176}
                />
              </ImageWrapper>
              <Stack>
                <Typography style={{ fontWeight: 700, fontSize: '1rem' }}>
                  {new_car_title}
                </Typography>
                {lowest_price === '0' && highest_price === '0' ? (
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      color: '#31ACBF',
                    }}
                  >
                    {'TBC'}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      overflow: 'hidden',
                      color: '#31ACBF',
                    }}
                  >
                    {`${priceFormat({
                      price: lowest_price,
                      prefix: 'RM ',
                    })} - ${priceFormat({
                      price: highest_price,
                      prefix: 'RM ',
                    })}`}
                  </Typography>
                )}
              </Stack>
            </Stack>
            {renderChip()}
          </Stack>
          <Stack direction="row" gap={4} zIndex={100}>
            <Button
              onClick={onOpenGetQuoteDialog}
              style={{
                borderRadius: 0,
                background: '#FFC800',
                color: '#1E242F',
                fontWeight: 700,
                fontSize: 13,
                textTransform: 'none',
                padding: '8px 30px',
                whiteSpace: 'nowrap',
              }}
            >
              Get Quote
            </Button>
          </Stack>
        </Stack>
      );
    }
    if (smUp) {
      return (
        <Stack
          style={{
            flexDirection: 'row',
            borderBottom: '1px solid #EDECED',
            padding: '16px 0',
            alignItems: 'center',
            gap: 16,
            position: 'relative',
          }}
        >
          <Stack
            direction="row"
            alignItems={'center'}
            style={{ gap: 16 }}
            {...clickProps}
          >
            <Box style={{ position: 'absolute', top: 0, left: 0 }}>
              <RankTag rank={rank ?? 0} />
            </Box>
            <ImageWrapper height={'92px'} width={'176px'}>
              <StyledImage
                src={car_model_image}
                alt={new_car_title}
                height={92}
                width={176}
              />
            </ImageWrapper>
          </Stack>
          <Stack
            style={{
              gap: 8,
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Stack {...clickProps} sx={{ alignItems: 'flex-start' }}>
              <Typography
                style={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {new_car_title}
              </Typography>
              {lowest_price === '0' && highest_price === '0' ? (
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    color: '#31ACBF',
                  }}
                >
                  {'TBC'}
                </Typography>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      overflow: 'hidden',
                      color: '#31ACBF',
                    }}
                  >{`${priceFormat({
                    price: lowest_price,
                    prefix: 'RM ',
                  })} - ${priceFormat({
                    price: highest_price,
                    prefix: 'RM ',
                  })}`}</Typography>
                </>
              )}
              {renderChip()}
            </Stack>
            <Stack direction="column" style={{ gap: 16 }}>
              <Button
                onClick={onOpenGetQuoteDialog}
                style={{
                  borderRadius: 0,
                  background: '#FFC800',
                  color: '#1E242F',
                  fontWeight: 700,
                  fontSize: 13,
                  textTransform: 'none',
                  padding: '8px 30px',
                  whiteSpace: 'nowrap',
                }}
              >
                Get Quote
              </Button>
            </Stack>
          </Stack>
        </Stack>
      );
    }
    return (
      <Stack
        style={{
          flexDirection: 'row',
          padding: '16px 0',
          alignItems: 'center',
          gap: 8,
          position: 'relative',
        }}
      >
        <Stack
          {...clickProps}
          direction="row"
          alignItems={'center'}
          style={{ gap: 8 }}
        >
          <Box style={{ position: 'absolute', top: 0, left: 0 }}>
            <RankTag rank={rank ?? 0} />
          </Box>
          <ImageWrapper height={'79px'} width={'140px'}>
            <StyledImage
              src={car_model_image}
              alt={new_car_title}
              height={79}
              width={140}
            />
          </ImageWrapper>
        </Stack>
        <Stack style={{ gap: 8, width: '100%' }}>
          <Stack
            {...clickProps}
            style={{
              gap: 4,
              alignItems: 'flex-start',
              textDecoration: 'none',
            }}
          >
            <Typography style={{ fontWeight: 700, fontSize: '1rem' }}>
              {new_car_title}
            </Typography>
            {lowest_price === '0' && highest_price === '0' ? (
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  color: '#31ACBF',
                }}
              >
                {'TBC'}
              </Typography>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    width: '100%',
                    overflow: 'hidden',
                    color: '#31ACBF',
                  }}
                >{`${priceFormat({
                  price: lowest_price,
                  prefix: 'RM ',
                })} - ${priceFormat({
                  price: highest_price,
                  prefix: 'RM ',
                })}`}</Typography>
              </>
            )}
            {renderChip()}
          </Stack>
          <Stack direction="column" style={{ gap: 8 }}>
            <Button
              onClick={onOpenGetQuoteDialog}
              style={{
                borderRadius: 0,
                background: '#FFC800',
                color: '#1E242F',
                fontWeight: 700,
                fontSize: 13,
                textTransform: 'none',
                padding: '8px 30px',
                whiteSpace: 'nowrap',
              }}
            >
              Get Quote
            </Button>
          </Stack>
        </Stack>
      </Stack>
    );
  };
  return (
    <>
      <QuoteDialog
        isOpen={isOpen}
        onClose={onClose}
        data={{
          carId: new_car_id,
          carImage: car_model_image,
          carName: new_car_title,
          carBrandImage: car_brand_image,
          lowestPrice: lowest_price,
          highestPrice: highest_price,
        }}
        location_state={location_state}
        location_city={location_city}
        webView={webView}
      />
      {renderContent()}
    </>
  );
};

/**
 * ===========================
 * EXPORT
 * ===========================
 */
export default TopModelItem;
